import {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
  type RefObject,
} from "react";

import { Info, MoveUpRight } from "lucide-react";
import { createPortal } from "react-dom";
import { useNavigate, useParams } from "react-router-dom";

import { useHealthScoreThresholds } from "../../Hooks/useHealthScoreThresholds";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../intelligence-briefing-library/components/ui/tooltip";
import type {
  NeedsAttentionCriteria,
  PortfolioSummary,
} from "../../Store/Reducers/MyPortfolioOverviewSlice";
import { DEFAULT_NEEDS_ATTENTION_CRITERIA } from "../../Store/Reducers/MyPortfolioOverviewSlice";
import {
  isRole,
  normalizeRoleName,
  ROLES,
} from "../../Utils/Constants/RoleConstants";
import {
  formatAccountHealthBandLegend,
  formatBusinessHealthBandLegend,
} from "../../Utils/HealthThresholds";
import { formatCompactCurrency } from "../../Utils/Helper/formatCompactCurrency";
import {
  getFromLocalStorage,
  STORAGE_KEYS,
} from "../../Utils/Helper/LocalStorage";
import { fanCapTone } from "../../Utils/Helper/rlFanCaps";

export type TileFilter =
  | "primary"
  | "secondary"
  | "executive"
  | "attention"
  | null;

export const TILE_FILTER_LABELS: Record<Exclude<TileFilter, null>, string> = {
  primary: "Primary Sponsor Assignments",
  secondary: "Secondary Sponsor Assignments",
  executive: "Executive Assignments",
  attention: "Needs Attention",
};

export function matchesExecutiveSponsorRole(roleType?: string | null): boolean {
  const role = (roleType || "").toLowerCase();
  return (
    role === "account_team" || role === "additional" || role === "extra_sponsor"
  );
}

type CapPopKind = "rel" | "attn" | "primary" | null;

interface CapPopPos {
  top: number;
  left: number;
}

interface Props {
  summary: PortfolioSummary;
  needsAttentionCriteria?: NeedsAttentionCriteria | null;
  activeTileFilter: TileFilter;
  onTileFilter: (filter: TileFilter) => void;
  onSwitchTab?: () => void;
  /** Total mapped pairs across portfolio (fan-out used). */
  fanOutUsed?: number;
  /** Portfolio fan-out soft limit (accounts × per-account fan-out cap). */
  fanOutLimit?: number | null;
  /** Max vendor contacts on any customer contact (fan-in used). */
  maxFanIn?: number;
  /** Org fan-in cap from RL settings. */
  fanInCap?: number;
}

const tileBase =
  "relative flex min-w-[148px] flex-1 flex-col items-center justify-center rounded-xl border border-gray-200 bg-white px-3 py-3.5";
const tileVal =
  "whitespace-nowrap text-center text-xl font-extrabold leading-none text-slate-900";
const tileLabel =
  "mt-1.5 text-center text-[14px] font-normal tracking-wide text-gray-500";
const tileLabelInline = "text-[14px] font-normal tracking-wide text-gray-500";
// const tileSub = "mt-0.5 text-center text-[11px] text-gray-400";
const tileTitleRow = "mt-1.5 flex items-center justify-center gap-1";
const cfgBtn =
  "cursor-pointer rounded p-0.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-purple-600";

const kpiInfoBtnClass =
  "inline-flex h-3.5 w-3.5 shrink-0 cursor-help items-center justify-center rounded-full border border-gray-200 bg-gray-50 text-gray-400 transition-colors hover:border-purple-300 hover:bg-purple-50 hover:text-purple-600 focus:outline-none focus-visible:border-purple-400 focus-visible:text-purple-600 data-[state=open]:border-purple-300 data-[state=open]:bg-purple-50 data-[state=open]:text-purple-600";

const kpiTooltipClass =
  "z-[9999] max-w-[260px] border border-gray-200 bg-white px-3 py-2.5 text-[12px] font-normal normal-case tracking-normal leading-relaxed text-gray-700 shadow-[0_4px_16px_rgba(0,0,0,0.12)]";

export function usesSponsorAssignmentKpi(
  userRole: string | { name?: string } | null | undefined
): boolean {
  const normalized = normalizeRoleName(userRole);
  if (!normalized) return false;
  return (
    normalized === ROLES.EXECUTIVE_SPONSOR ||
    normalized === ROLES.EXECUTIVE_SPONSOR_LEAD ||
    normalized === "sponsor"
  );
}

const SPONSOR_ASSIGNMENT_KPI_INFO = {
  primary: "Accounts where you are assigned as the primary executive sponsor.",
  secondary:
    "Accounts where you are assigned as the secondary executive sponsor.",
  executive:
    "Accounts where you are on the account team or assigned as an additional executive sponsor (not primary or secondary).",
} as const;

const PORTFOLIO_ASSIGNMENT_KPI_INFO = {
  primary:
    "Accounts in your portfolio that have a primary executive sponsor assigned.",
  secondary:
    "Accounts in your portfolio that have a secondary executive sponsor assigned.",
  executive:
    "Total additional executives across your portfolio accounts (excludes primary and secondary).",
} as const;

const KPI_INFO = {
  attention:
    "Primary + Secondary accounts flagged by your Needs Attention rules. Sponsor Assignment accounts are excluded from this KPI. Use the arrow to filter; hover the info icon for criteria details.",
  relationshipCapacity:
    "Total mapped relationship pairs (fan-out) versus portfolio capacity, and max contact coverage (fan-in) versus the org cap. Hover the info icon for details.",
  totalAccounts: "Total accounts in your portfolio for the current scope.",
  revenue: "Combined revenue across accounts in your portfolio.",
} as const;

const KpiCardTip = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <Tooltip>
    <TooltipTrigger asChild>{children}</TooltipTrigger>
    <TooltipContent
      side="bottom"
      align="center"
      sideOffset={8}
      className={kpiTooltipClass}
    >
      {label}
    </TooltipContent>
  </Tooltip>
);

const KpiInfoTip = ({
  label,
  tooltip,
  buttonRef,
  onHoverOpen,
  onHoverClose,
}: {
  label: string;
  tooltip: string;
  buttonRef?: RefObject<HTMLButtonElement | null>;
  /** When set, opens the detail panel on hover instead of a short tooltip. */
  onHoverOpen?: () => void;
  onHoverClose?: () => void;
}) => {
  if (onHoverOpen) {
    return (
      <button
        ref={buttonRef}
        type="button"
        className={kpiInfoBtnClass}
        aria-label={`About ${label}`}
        onMouseEnter={onHoverOpen}
        onMouseLeave={() => onHoverClose?.()}
        onFocus={onHoverOpen}
        onBlur={() => onHoverClose?.()}
      >
        <Info className="h-2.5 w-2.5" strokeWidth={2.5} aria-hidden />
      </button>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          ref={buttonRef}
          type="button"
          className={kpiInfoBtnClass}
          aria-label={`About ${label}`}
          onClick={e => e.stopPropagation()}
        >
          <Info className="h-2.5 w-2.5" strokeWidth={2.5} aria-hidden />
        </button>
      </TooltipTrigger>
      <TooltipContent
        side="bottom"
        align="end"
        sideOffset={6}
        className={kpiTooltipClass}
      >
        {tooltip}
      </TooltipContent>
    </Tooltip>
  );
};

const CriterionRow = ({
  active,
  label,
  detail,
}: {
  active: boolean;
  label: string;
  detail?: string;
}) => (
  <div
    className={`mb-2 flex items-start gap-2 text-[12px] last:mb-0 ${
      active ? "text-gray-800" : "text-gray-400"
    }`}
  >
    <span
      className={`mt-0.5 inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-sm text-[9px] font-bold ${
        active ? "bg-purple-100 text-purple-700" : "bg-gray-100 text-gray-400"
      }`}
    >
      {active ? "✓" : "–"}
    </span>
    <div className="font-semibold leading-snug">
      {label}
      {detail ? (
        <span className="font-normal text-gray-400"> ({detail})</span>
      ) : null}
    </div>
  </div>
);

const PortfolioSummaryStrip = ({
  summary,
  needsAttentionCriteria,
  activeTileFilter,
  onTileFilter,
  onSwitchTab,
  fanOutUsed: fanOutUsedProp,
  fanOutLimit: fanOutLimitProp = null,
  maxFanIn = 0,
  fanInCap = 0,
}: Props) => {
  const navigate = useNavigate();
  const { thresholds } = useHealthScoreThresholds();
  const { orgSlug } = useParams<{ orgSlug?: string }>();
  const settingsPath = orgSlug ? `/${orgSlug}/settings` : "/settings";
  const criteria = needsAttentionCriteria ?? DEFAULT_NEEDS_ATTENTION_CRITERIA;
  const loggedInUser = getFromLocalStorage(STORAGE_KEYS.LOGGED_IN_USER);
  const sponsorAssignmentKpi = usesSponsorAssignmentKpi(loggedInUser?.role);
  const assignmentKpiInfo = sponsorAssignmentKpi
    ? SPONSOR_ASSIGNMENT_KPI_INFO
    : PORTFOLIO_ASSIGNMENT_KPI_INFO;
  const canOpenSettings =
    isRole(loggedInUser?.role, ROLES.VENDOR_ADMIN) ||
    isRole(loggedInUser?.role, ROLES.EXECUTIVE_SPONSOR_LEAD);
  /** Capacity `used / limit` is for Executive Sponsor only. */
  const showPrimaryCapacityRatio =
    isRole(loggedInUser?.role, ROLES.EXECUTIVE_SPONSOR) ||
    isRole(loggedInUser?.role, ROLES.EXECUTIVE_SPONSOR_LEAD);

  const bandDetails = useMemo(() => {
    const ah = formatAccountHealthBandLegend(thresholds.account_health);
    const bh = formatBusinessHealthBandLegend(thresholds.business_health);
    return {
      triage: ah.triage.replace(/^Triage\s+/i, "Account Health "),
      defend: ah.defend.replace(/^Defend\s+/i, "Account Health "),
      bhConcern: bh.concern.replace(/^Concern\s+/i, "Business Health "),
    };
  }, [thresholds]);

  const [openPop, setOpenPop] = useState<CapPopKind>(null);
  const [popPos, setPopPos] = useState<CapPopPos>({ top: 0, left: 0 });
  const [primaryTipOpen, setPrimaryTipOpen] = useState(false);
  const [primaryInfoHover, setPrimaryInfoHover] = useState(false);
  const relBtnRef = useRef<HTMLButtonElement>(null);
  const attnBtnRef = useRef<HTMLButtonElement>(null);
  const primaryBtnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const closePopTimerRef = useRef<number | null>(null);

  const placePop = useCallback((btn: HTMLElement | null) => {
    if (!btn) return;
    const tile = btn.closest("[data-strip-tile]") as HTMLElement | null;
    const rect = (tile || btn).getBoundingClientRect();
    setPopPos({
      top: rect.bottom + 8,
      left: Math.min(rect.left, window.innerWidth - 340),
    });
  }, []);

  const cancelClosePop = useCallback(() => {
    if (closePopTimerRef.current != null) {
      window.clearTimeout(closePopTimerRef.current);
      closePopTimerRef.current = null;
    }
  }, []);

  const openCapPop = useCallback(
    (kind: Exclude<CapPopKind, null>, btn: HTMLButtonElement | null) => {
      cancelClosePop();
      placePop(btn);
      setOpenPop(kind);
    },
    [cancelClosePop, placePop]
  );

  const scheduleCloseCapPop = useCallback(() => {
    cancelClosePop();
    closePopTimerRef.current = window.setTimeout(() => {
      setOpenPop(null);
      closePopTimerRef.current = null;
    }, 160);
  }, [cancelClosePop]);

  useEffect(() => {
    return () => {
      if (closePopTimerRef.current != null) {
        window.clearTimeout(closePopTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!openPop) return;

    const onDocClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (popRef.current?.contains(t)) return;
      if (relBtnRef.current?.contains(t)) return;
      if (attnBtnRef.current?.contains(t)) return;
      if (primaryBtnRef.current?.contains(t)) return;
      setOpenPop(null);
    };
    const onScrollOrResize = () => setOpenPop(null);

    const timer = window.setTimeout(() => {
      document.addEventListener("mousedown", onDocClick);
    }, 0);
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("mousedown", onDocClick);
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [openPop]);

  const fanOutUsed = fanOutUsedProp ?? summary.relationship_pairs_total;
  const fanOutLimit =
    fanOutLimitProp != null && fanOutLimitProp > 0 ? fanOutLimitProp : null;
  const fanInUsed = maxFanIn;
  const fanInLimit = fanInCap > 0 ? fanInCap : null;

  const fanOutPct =
    fanOutLimit != null && fanOutLimit > 0
      ? Math.min(100, Math.round((fanOutUsed / fanOutLimit) * 100))
      : 0;
  const fanInPct =
    fanInLimit != null && fanInLimit > 0
      ? Math.min(100, Math.round((fanInUsed / fanInLimit) * 100))
      : 0;

  const fanOutTone =
    fanOutLimit != null ? fanCapTone(fanOutUsed, fanOutLimit) : "ok";
  const fanInTone =
    fanInLimit != null ? fanCapTone(fanInUsed, fanInLimit) : "ok";

  /** Fan-out bar: amber when approaching limit, red when over. */
  const fanOutBarClass = fanOutTone === "over" ? "bg-red-500" : "bg-orange-400";
  const fanOutCountClass =
    fanOutTone === "over" ? "text-red-600" : "text-slate-900";
  const fanInBarClass =
    fanInTone === "over"
      ? "bg-red-500"
      : fanInTone === "warn"
        ? "bg-orange-400"
        : "bg-emerald-500";
  const fanInCountClass =
    fanInTone === "over"
      ? "text-red-600"
      : fanInTone === "warn"
        ? "text-orange-500"
        : "text-emerald-600";

  const acctUsed = summary.account_capacity_used;
  const acctLimit = summary.account_capacity_limit;
  const acctPct =
    acctLimit != null && acctLimit > 0
      ? Math.min(100, Math.round((acctUsed / acctLimit) * 100))
      : 0;
  const acctTone =
    acctLimit != null && acctLimit > 0 ? fanCapTone(acctUsed, acctLimit) : "ok";
  const acctBarWarn = acctPct >= 75;
  const acctCountClass =
    acctTone === "over" ? "text-red-600" : "text-slate-900";

  const handleAttentionClick = () => {
    onTileFilter(activeTileFilter === "attention" ? null : "attention");
    onSwitchTab?.();
  };

  // const attentionSub =
  //   summary.unmapped_accounts > 0
  //     ? `${summary.unmapped_accounts} unmapped`
  //     : null;

  const renewalActive = (criteria.renewal_days || 0) > 0;
  const healthActive = (criteria.health_threshold || 0) > 0;

  const capPopover =
    openPop &&
    createPortal(
      <div
        ref={popRef}
        className="fixed z-[1300] min-w-[300px] max-w-[340px] rounded-xl border border-gray-200 bg-white p-4 shadow-lg"
        style={{ top: popPos.top, left: popPos.left }}
        onMouseEnter={cancelClosePop}
        onMouseLeave={scheduleCloseCapPop}
      >
        {openPop === "attn" ? (
          <>
            <div className="mb-3 flex items-center justify-between text-[13px] font-extrabold text-gray-900">
              <span>Needs Attention Criteria</span>
              <button
                type="button"
                className="cursor-pointer border-none bg-transparent text-sm text-gray-500"
                onClick={() => setOpenPop(null)}
              >
                ✕
              </button>
            </div>
            <p className="mb-3 text-[11px] leading-snug text-gray-500">
              Accounts matching <strong>any</strong> active rule are flagged.
              Configured in Settings.
            </p>
            <div className="mb-3 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2.5">
              <CriterionRow
                active={healthActive}
                label={
                  healthActive
                    ? `Account Health below ${criteria.health_threshold}`
                    : "Account Health threshold"
                }
                detail={healthActive ? undefined : "Off"}
              />
              <CriterionRow
                active={renewalActive}
                label={
                  renewalActive
                    ? `Renewal within ${criteria.renewal_days} days`
                    : "Renewal proximity"
                }
                detail={renewalActive ? undefined : "Off"}
              />
              <CriterionRow
                active={criteria.include_triage}
                label="Triage — include all Triage cluster accounts"
                detail={
                  criteria.include_triage ? bandDetails.triage : "Not included"
                }
              />
              <CriterionRow
                active={criteria.include_defend}
                label="Defend — include all Defend cluster accounts"
                detail={
                  criteria.include_defend ? bandDetails.defend : "Not included"
                }
              />
              <CriterionRow
                active={criteria.include_bh_concern}
                label="Include all BH Concern accounts"
                detail={
                  criteria.include_bh_concern
                    ? bandDetails.bhConcern
                    : "Not included"
                }
              />
              <CriterionRow
                active={criteria.include_unscored}
                label="Include all Unavailable accounts"
                detail={
                  criteria.include_unscored
                    ? "no KPI data / no Account Health score"
                    : "Not included"
                }
              />
              <CriterionRow
                active={criteria.include_unmapped}
                label="0 pairs mapped (My RL)"
                detail={
                  criteria.include_unmapped
                    ? "No mapped relationships"
                    : "Not included"
                }
              />
              <CriterionRow
                active={criteria.include_unknown_al}
                label="No AL assessment (AL ?)"
                detail={
                  criteria.include_unknown_al
                    ? "Missing Account Level (AL ?)"
                    : "Not included"
                }
              />
            </div>
            <div className="flex items-center justify-between gap-2">
              <div className="text-[11px] text-gray-400">
                {summary.needs_attention_count} account
                {summary.needs_attention_count === 1 ? "" : "s"} match
              </div>
              {canOpenSettings ? (
                <button
                  type="button"
                  className="cursor-pointer border-none bg-transparent p-0 text-[12px] font-semibold text-purple-600 hover:text-purple-800"
                  onClick={() => {
                    setOpenPop(null);
                    navigate(settingsPath);
                  }}
                >
                  Settings ↗
                </button>
              ) : (
                <div className="text-[11px] text-gray-400">
                  Set by program admin
                </div>
              )}
            </div>
          </>
        ) : openPop === "primary" ? (
          <>
            <div className="mb-3 flex items-center justify-between">
              <span className="text-[14px] font-normal text-gray-900">
                Account Capacity
              </span>
              <button
                type="button"
                className="cursor-pointer border-none bg-transparent text-sm text-gray-500"
                onClick={() => setOpenPop(null)}
              >
                ✕
              </button>
            </div>
            <div className="mb-1.5 text-[10px] font-extrabold uppercase tracking-wider text-gray-400">
              Primary Accounts
            </div>
            <div className="mb-3 flex items-center gap-3">
              <div className="h-2.5 min-w-0 flex-1 overflow-hidden rounded-full bg-gray-100">
                <div
                  className={`h-full rounded-full transition-all ${
                    acctTone === "over"
                      ? "bg-red-500"
                      : acctBarWarn
                        ? "bg-orange-500"
                        : "bg-orange-400"
                  }`}
                  style={{ width: `${acctPct}%` }}
                />
              </div>
              <span className="shrink-0 text-[13px] font-bold tabular-nums">
                <span
                  className={
                    acctTone === "over"
                      ? "text-red-600"
                      : acctBarWarn
                        ? "text-orange-600"
                        : "text-orange-500"
                  }
                >
                  {acctUsed}
                </span>
                {acctLimit != null ? (
                  <span className="text-gray-400"> / {acctLimit}</span>
                ) : null}
              </span>
            </div>
            <div className="flex items-start gap-1.5 text-[11px] leading-snug text-gray-400">
              <Info
                className="mt-0.5 h-3 w-3 shrink-0 text-gray-400"
                strokeWidth={2.5}
                aria-hidden
              />
              <span>
                Soft limit based on C-level grade · configured by your program
                admin
              </span>
            </div>
          </>
        ) : (
          <>
            <div className="mb-3 flex items-center justify-between">
              <span className="text-[13px] font-extrabold text-gray-900">
                Relationship Capacity
              </span>
              <button
                type="button"
                className="cursor-pointer border-none bg-transparent text-sm text-gray-500"
                onClick={() => setOpenPop(null)}
              >
                ✕
              </button>
            </div>
            <div className="mb-4">
              <div className="mb-1.5 text-[10px] font-extrabold uppercase tracking-wider text-gray-400">
                Relationship Pairs (Fan-out)
              </div>
              <div className="mb-1.5 flex items-center gap-3">
                <div className="h-2.5 min-w-0 flex-1 overflow-hidden rounded-full bg-gray-100">
                  <div
                    className={`h-full rounded-full transition-all ${fanOutBarClass}`}
                    style={{ width: `${fanOutPct}%` }}
                  />
                </div>
                <span
                  className={`shrink-0 text-[13px] font-bold tabular-nums ${fanOutCountClass}`}
                >
                  {fanOutLimit != null
                    ? `${fanOutUsed} / ${fanOutLimit}`
                    : fanOutUsed}
                </span>
              </div>
              <div className="text-[11px] leading-snug text-gray-400">
                Total mapped pairs across all accounts · per-account counts
                visible on each row
              </div>
            </div>
            <div>
              <div className="mb-1.5 text-[10px] font-extrabold uppercase tracking-wider text-gray-400">
                Fan-in (Max Contact Coverage)
              </div>
              <div className="mb-1.5 flex items-center gap-3">
                <div className="h-2.5 min-w-0 flex-1 overflow-hidden rounded-full bg-gray-100">
                  <div
                    className={`h-full rounded-full transition-all ${fanInBarClass}`}
                    style={{ width: `${fanInPct}%` }}
                  />
                </div>
                <span
                  className={`shrink-0 text-[13px] font-bold tabular-nums ${fanInCountClass}`}
                >
                  {fanInLimit != null
                    ? `${fanInUsed} / ${fanInLimit}`
                    : fanInUsed}
                </span>
              </div>
              <div className="text-[11px] leading-snug text-gray-400">
                Max number of vendor contacts mapped to any single customer
                contact
                {fanInLimit != null ? ` · cap = ${fanInLimit}` : ""}
              </div>
            </div>
          </>
        )}
      </div>,
      document.body
    );

  return (
    <TooltipProvider delayDuration={150}>
      <div className="flex shrink-0 gap-2 overflow-x-auto pt-1 pb-1">
        <KpiCardTip label={KPI_INFO.totalAccounts}>
          <div className={tileBase} data-strip-tile>
            <div className={tileVal}>{summary.total_accounts}</div>
            <div className={tileLabel}>Total Accounts</div>
          </div>
        </KpiCardTip>
        {showPrimaryCapacityRatio && summary.account_capacity_limit != null ? (
          <Tooltip
            open={primaryTipOpen && !primaryInfoHover && openPop !== "primary"}
            onOpenChange={open => {
              if (primaryInfoHover || openPop === "primary") {
                setPrimaryTipOpen(false);
                return;
              }
              setPrimaryTipOpen(open);
            }}
          >
            <TooltipTrigger asChild>
              <div className={tileBase} data-strip-tile>
                <div className="relative z-10 flex flex-col items-center">
                  <div className={tileVal}>
                    <span className={acctCountClass}>
                      {summary.account_capacity_used}
                    </span>
                    <span className="text-[13px] font-semibold text-gray-400">
                      {" "}
                      / {summary.account_capacity_limit}
                    </span>
                  </div>
                  <div className={tileTitleRow}>
                    <span className={tileLabelInline}>Primary Assignments</span>
                    <KpiInfoTip
                      label="Primary Assignments"
                      tooltip={assignmentKpiInfo.primary}
                      buttonRef={primaryBtnRef}
                      onHoverOpen={() => {
                        setPrimaryInfoHover(true);
                        setPrimaryTipOpen(false);
                        openCapPop("primary", primaryBtnRef.current);
                      }}
                      onHoverClose={() => {
                        setPrimaryInfoHover(false);
                        scheduleCloseCapPop();
                      }}
                    />
                  </div>
                </div>
              </div>
            </TooltipTrigger>
            <TooltipContent
              side="bottom"
              align="center"
              sideOffset={8}
              className={kpiTooltipClass}
            >
              {assignmentKpiInfo.primary}
            </TooltipContent>
          </Tooltip>
        ) : (
          <KpiCardTip label={assignmentKpiInfo.primary}>
            <div className={tileBase} data-strip-tile>
              <div className={tileVal}>{summary.primary_count}</div>
              <div className={tileLabel}>Primary Assignments</div>
            </div>
          </KpiCardTip>
        )}
        <KpiCardTip label={assignmentKpiInfo.secondary}>
          <div className={tileBase} data-strip-tile>
            <div className={tileVal}>{summary.secondary_count}</div>
            <div className={tileLabel}>Secondary Assignments</div>
          </div>
        </KpiCardTip>
        <KpiCardTip label={assignmentKpiInfo.executive}>
          <div className={tileBase} data-strip-tile>
            <div className={tileVal}>{summary.executive_count}</div>
            <div className={tileLabel}>Executive Assignments</div>
          </div>
        </KpiCardTip>
        <KpiCardTip label={KPI_INFO.revenue}>
          <div className={tileBase} data-strip-tile>
            <div className={tileVal}>
              {formatCompactCurrency(summary.total_revenue, {
                fallback: "$0",
                zeroValue: "$0",
              })}
            </div>
            <div className={tileLabel}>Total Revenue</div>
          </div>
        </KpiCardTip>
        <div className={tileBase} data-strip-tile>
          <div className="flex items-center justify-center gap-1">
            <div className={`${tileVal} text-red-600`}>
              {summary.needs_attention_count}
            </div>
            <button
              type="button"
              className={`${cfgBtn} text-purple-600`}
              title="Filter to needs attention accounts"
              onClick={handleAttentionClick}
            >
              <MoveUpRight className="h-3.5 w-3.5" aria-hidden />
            </button>
          </div>
          <div className={tileTitleRow}>
            <span className={tileLabelInline}>Needs Attention</span>
            <KpiInfoTip
              label="Needs Attention"
              tooltip={KPI_INFO.attention}
              buttonRef={attnBtnRef}
              onHoverOpen={() => openCapPop("attn", attnBtnRef.current)}
              onHoverClose={scheduleCloseCapPop}
            />
          </div>
          {/* Temporarily hidden — restore unmapped subtitle later
          {attentionSub ? (
            <div className={tileSub}>· {attentionSub}</div>
          ) : null}
          */}
        </div>
        <div className={tileBase} data-strip-tile>
          <div className={tileVal}>
            <span
              className={
                fanOutTone === "over" ? "text-red-600" : "text-slate-900"
              }
            >
              {fanOutUsed}
            </span>
            {fanOutLimit != null ? (
              <span className="text-[13px] font-semibold text-gray-400">
                {" "}
                / {fanOutLimit}
              </span>
            ) : null}
          </div>
          <div className={tileTitleRow}>
            <span className={tileLabelInline}>Relationship Capacity</span>
            <KpiInfoTip
              label="Relationship Capacity"
              tooltip={KPI_INFO.relationshipCapacity}
              buttonRef={relBtnRef}
              onHoverOpen={() => openCapPop("rel", relBtnRef.current)}
              onHoverClose={scheduleCloseCapPop}
            />
          </div>
        </div>

        {capPopover}
      </div>
    </TooltipProvider>
  );
};

export default PortfolioSummaryStrip;
